import { useState } from "react";
import { createPortal } from "react-dom";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Form } from "react-bootstrap";

import { Card } from "@bagel-web/components";

import { supportedComponent } from "./supportedComponents";
import { PlaygroundComponentConfig } from "../../types";

// One presentable component in the Presentation Order list: dragged to
// reorder while presented, switched off to hide it (its presentation entry
// is dropped), with an element picker when its type presents more than one.
const PresentationRow = ({
  componentId,
  element,
  elements,
  onToggle,
  onElementChange,
}: {
  componentId: string;
  // The presented element, or null while the component is hidden.
  element: string | null;
  elements: Array<string>;
  onToggle: () => void;
  onElementChange: (element: string) => void;
}) => {
  const presented = element !== null;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: componentId, disabled: !presented });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : undefined,
      }}
      className="border rounded p-2 mb-2 d-flex align-items-center gap-3"
      data-testid={`playground-presentation-row-${componentId}`}
    >
      {/* Hidden components hold no slot in the order, so nothing to drag;
          the invisible handle keeps the rows aligned. */}
      <button
        type="button"
        className={`btn p-0 border-0 text-body-secondary${
          presented ? "" : " invisible"
        }`}
        style={{ cursor: "grab" }}
        data-testid={`playground-presentation-drag-${componentId}`}
        {...attributes}
        {...listeners}
      >
        <i className="bi-grip-vertical" />
      </button>
      <code>{componentId}</code>
      {presented &&
        (elements.length > 1 ? (
          <Form.Select
            size="sm"
            className="font-monospace w-auto"
            aria-label={`${componentId} presentation element`}
            value={element}
            onChange={(event) => onElementChange(event.target.value)}
            data-testid={`playground-presentation-element-${componentId}`}
          >
            {elements.map((candidate) => (
              <option key={candidate} value={candidate}>
                {candidate}
              </option>
            ))}
          </Form.Select>
        ) : (
          <code className="text-body-secondary">{element}</code>
        ))}
      <Form.Check
        type="switch"
        id={`playground-presentation-visible-${componentId}`}
        label="Visible"
        className="ms-auto text-nowrap"
        checked={presented}
        disabled={!presented && elements.length === 0}
        onChange={onToggle}
        data-testid={`playground-presentation-visible-${componentId}`}
      />
    </div>
  );
};

// The Presentation card: the presented components in presentation order
// with the hidden ones trailing, reordered by drag, toggled visible, and
// switched between their type's elements. Every change hands back the
// whole presentation order.
const PresentationCard = ({
  componentConfigs,
  presentationOrder,
  onOrderChange,
}: {
  componentConfigs: Array<PlaygroundComponentConfig>;
  presentationOrder: Array<[string, string]>;
  onOrderChange: (order: Array<[string, string]>) => void;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // The Presentation list: presented components in presentation order, the
  // hidden ones trailing in config order.
  const presentationRows = [
    ...presentationOrder.flatMap(([componentId, element]) => {
      const component = componentConfigs.find(
        (candidate) => candidate.id === componentId
      );
      return component
        ? [{ component, element: element as string | null }]
        : [];
    }),
    ...componentConfigs
      .filter(
        (component) =>
          typeof component.id === "string" &&
          !presentationOrder.some(
            ([componentId]) => componentId === component.id
          )
      )
      .map((component) => ({ component, element: null as string | null })),
  ];

  // A component's presentable elements: the registry's for known types;
  // for a type the client doesn't know, whatever its seeded entry names.
  const presentableElements = (
    component: PlaygroundComponentConfig,
    element: string | null
  ) =>
    supportedComponent(component.type)?.elements ??
    (element !== null ? [element] : []);

  // Hiding drops the component's entry; showing appends one presenting the
  // type's default element.
  const togglePresented = (componentId: string, elements: Array<string>) => {
    const without = presentationOrder.filter(([id]) => id !== componentId);
    if (without.length < presentationOrder.length) {
      onOrderChange(without);
    } else if (elements.length > 0) {
      onOrderChange([...presentationOrder, [componentId, elements[0]!]]);
    }
  };

  const setPresentedElement = (componentId: string, element: string) =>
    onOrderChange(
      presentationOrder.map(([id, current]): [string, string] =>
        id === componentId ? [id, element] : [id, current]
      )
    );

  // The dragged row renders again in the overlay; while it is set, the
  // source row stays put in the list.
  const [dragComponentId, setDragComponentId] = useState<string | null>(null);
  const dragRow = presentationRows.find(
    ({ component }) => component.id === dragComponentId
  );

  const handleDragStart = (event: DragStartEvent) => {
    setDragComponentId(
      typeof event.active.id === "string" ? event.active.id : null
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDragComponentId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const from = presentationOrder.findIndex(([id]) => id === active.id);
    const to = presentationOrder.findIndex(([id]) => id === over.id);
    if (from < 0 || to < 0) {
      return;
    }
    onOrderChange(arrayMove(presentationOrder, from, to));
  };

  return (
    <Card title="Presentation">
      <h6>Presentation Order</h6>
      {presentationRows.length ? (
        <div data-testid="playground-presentation-order">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragCancel={() => setDragComponentId(null)}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={presentationOrder.map(([componentId]) => componentId)}
              strategy={verticalListSortingStrategy}
            >
              {presentationRows.map(({ component, element }) => {
                const componentId =
                  typeof component.id === "string" ? component.id : "";
                const elements = presentableElements(component, element);
                return (
                  <PresentationRow
                    key={componentId}
                    componentId={componentId}
                    element={element}
                    elements={elements}
                    onToggle={() => togglePresented(componentId, elements)}
                    onElementChange={(next) =>
                      setPresentedElement(componentId, next)
                    }
                  />
                );
              })}
            </SortableContext>
            {/* The floating copy lives in a body-level portal, outside the
                card, so dragging never grows the card's scroll extents;
                with an overlay present, dnd-kit leaves the source row in
                place. */}
            {createPortal(
              // No drop animation: the source row never left the list, so
              // a copy snapping back onto it reads as a glitch.
              <DragOverlay dropAnimation={null}>
                {dragRow ? (
                  <div
                    className="border rounded p-2 d-flex align-items-center gap-3 bg-body"
                    data-testid="playground-presentation-drag-overlay"
                  >
                    <i className="bi-grip-vertical text-body-secondary" />
                    <code>{dragComponentId}</code>
                    {typeof dragRow.element === "string" && (
                      <code className="text-body-secondary">
                        {dragRow.element}
                      </code>
                    )}
                  </div>
                ) : null}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        </div>
      ) : (
        <p className="text-body-secondary mb-0">No components.</p>
      )}
    </Card>
  );
};

export default PresentationCard;
